/**
 * Lo único que este paquete guarda fuera de memoria (docs/FASE-7.md §6.2): la transacción de
 * autorización en curso —`state`, `code_verifier` y a dónde volver— y una marca de «ya intenté
 * entrar en silencio».
 *
 * **Ninguna de las dos cosas es una credencial.** El `code_verifier` es un secreto de un solo uso
 * que tiene que sobrevivir exactamente a una navegación, y por eso vive en `sessionStorage`: muere
 * con la pestaña, no viaja a otras, y no sirve de nada sin el código que llega por la URL. Los
 * tokens, en cambio, no se guardan en ningún sitio.
 *
 * Todo acceso va envuelto en `try/catch`: en una pestaña privada, con cookies bloqueadas o con el
 * almacenamiento lleno, `sessionStorage` **lanza**. Un fallo ahí no puede tumbar el arranque de la
 * aplicación; como mucho hace que el usuario vea la pantalla de acceso.
 */
const TRANSACTION_KEY = "cnt:auth:tx";
const SILENT_TRIED_KEY = "cnt:auth:silent-tried";
/** Una transacción más vieja que esto se descarta: es una pestaña olvidada, no un flujo en curso. */
export const TRANSACTION_TTL_MS = 10 * 60 * 1000;
export function saveTransaction(storage, tx) {
    try {
        storage.setItem(TRANSACTION_KEY, JSON.stringify(tx));
    }
    catch {
        // Sin almacenamiento no hay flujo posible, pero reventar aquí dejaría la aplicación en blanco:
        // el callback fallará por `state` ausente y el usuario verá la pantalla de acceso, que es el
        // peor resultado aceptable.
    }
}
/**
 * Lee y **consume** la transacción: una autorización se canjea una sola vez, así que dejarla ahí
 * solo daría oportunidades a un segundo intento con el mismo `state`.
 */
export function takeTransaction(storage, now) {
    let raw;
    try {
        raw = storage.getItem(TRANSACTION_KEY);
        storage.removeItem(TRANSACTION_KEY);
    }
    catch {
        return null;
    }
    if (raw === null)
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null)
            return null;
        const tx = parsed;
        if (typeof tx.state !== "string" || typeof tx.codeVerifier !== "string" || typeof tx.createdAt !== "number") {
            return null;
        }
        if (now - tx.createdAt > TRANSACTION_TTL_MS)
            return null;
        return {
            state: tx.state,
            codeVerifier: tx.codeVerifier,
            returnTo: typeof tx.returnTo === "string" ? tx.returnTo : "/",
            createdAt: tx.createdAt,
            silent: tx.silent === true,
        };
    }
    catch {
        return null;
    }
}
/**
 * Marca de intento silencioso. Es el freno anti-bucle de §6.4: un intento que falla no puede
 * provocar otro, porque un bucle de redirecciones entre módulo e IdP es, desde fuera,
 * indistinguible de una caída.
 */
export function markSilentTried(storage) {
    try {
        storage.setItem(SILENT_TRIED_KEY, "1");
    }
    catch {
        // Sin marca, el peor caso es un intento silencioso de más por carga de página. No es un bucle:
        // cada intento vuelve con error y el flujo termina en `anonymous`.
    }
}
export function wasSilentTried(storage) {
    try {
        return storage.getItem(SILENT_TRIED_KEY) === "1";
    }
    catch {
        return false;
    }
}
/** Se limpia al autenticarse: la próxima sesión del navegador vuelve a tener derecho a intentarlo. */
export function clearSilentTried(storage) {
    try {
        storage.removeItem(SILENT_TRIED_KEY);
    }
    catch {
        // Nada que hacer; ver `markSilentTried`.
    }
}
/** Almacenamiento de reserva cuando no hay `sessionStorage` (SSR, pestaña privada muy estricta). */
export function createMemoryStorage() {
    const map = new Map();
    return {
        getItem: (key) => map.get(key) ?? null,
        setItem: (key, value) => {
            map.set(key, value);
        },
        removeItem: (key) => {
            map.delete(key);
        },
    };
}
//# sourceMappingURL=storage.js.map